import { z } from 'zod';

import {
  legalClientDocumentFileSchema,
  legalClientDocumentVersionSchema,
} from './ILegalClientDocumentVersion';

/**
 * GwG-Ordner eines Vorgangs — eine *Sicht* auf die GwG-relevanten Dokumentarten
 * der beteiligten juristischen Personen, kein eigener Dokumentenspeicher.
 *
 * Die Unterlagen liegen weiterhin am Mandanten als
 * `LegalClient → Dokumenttyp → Version → Dateien`. Der GwG-Ordner ergänzt sie um
 * Pflicht-/Optional-Status, Erinnerungen und die Vollständigkeitskontrolle.
 *
 * Wie überall im Vorgang wirkt jede Änderung erst beim Speichern; bis dahin
 * arbeitet die Oberfläche mit negativen temporären IDs.
 */

/** Erinnerungsarten. */
export const GWG_REMINDER_MODE_DATE = 'date';
export const GWG_REMINDER_MODE_MEETING = 'meeting';

export type IGwGReminderMode =
  | typeof GWG_REMINDER_MODE_DATE
  | typeof GWG_REMINDER_MODE_MEETING;

/** Sentinel in `LegalClient_idLegalClient`: Standard des Ordners statt Override. */
export const GWG_REQUIREMENT_FOLDER_DEFAULT = 0;

export const gwgReminderSchema = z.object({
  /** Negativ bei neuen Erinnerungen. */
  idGwGReminder: z.number(),
  idGwGFolderRequirement: z.number().nullable().optional(),
  ReminderMode: z.string(),
  /** Bei `ReminderMode = 'date'`: das feste Datum. */
  RemindAt: z.string().nullable().optional(),
  /** Bei `ReminderMode = 'meeting'`: der Termin, an dem die Erinnerung hängt. */
  idLegalTransactionMeeting: z.number().nullable().optional(),
  LegalTransactionMeeting: z.string().nullable().optional(),
  /** Tage vor dem Termin; 0 = am Tag des Termins. */
  OffsetDays: z.number(),
  /** Fälligkeitsdatum, für das schon erinnert wurde. */
  SentFor: z.string().nullable().optional(),
  /** Vom Backend berechnet — bei Terminbezug aus Termin minus `OffsetDays`. */
  DueDate: z.string().nullable().optional(),
});

/** Eine Zeile der Ansicht: ein Beteiligter zu einer Dokumentart. */
export const gwgParticipantSchema = z.object({
  idLegalClient: z.number(),
  LegalClient: z.string(),
  /** Anforderung, die für diesen Beteiligten gilt (Override oder Standard). */
  idGwGFolderRequirement: z.number(),
  isMandatory: z.boolean(),
  /** Hat dieser Beteiligte eine eigene, abweichende Anforderung? */
  hasOverride: z.boolean(),
  hasDocument: z.boolean(),
  CurrentVersion: legalClientDocumentVersionSchema.nullable(),
  VersionArray: z.array(legalClientDocumentVersionSchema),
  ReminderArray: z.array(gwgReminderSchema),
});

/** Eine Dokumentart mit den Zeilen aller beteiligten juristischen Personen. */
export const gwgDocumentTypeSchema = z.object({
  idLegalClientDocumentType: z.number(),
  LegalClientDocumentType: z.string(),
  /** Standardzeile des Ordners (`LegalClient_idLegalClient = 0`). */
  idGwGFolderRequirement: z.number(),
  isMandatoryDefault: z.boolean(),
  ReminderArray: z.array(gwgReminderSchema),
  ParticipantArray: z.array(gwgParticipantSchema),
  /** Anzahl der Beteiligten, deren Pflichtunterlage fehlt. */
  OpenCount: z.number(),
  isFulfilled: z.boolean(),
});

export const gwgCompletenessSchema = z.object({
  mandatoryTotal: z.number(),
  mandatoryFulfilled: z.number(),
  missing: z.number(),
  isComplete: z.boolean(),
});

export const gwgFolderSchema = z.object({
  idDocumentDirectory: z.number(),
  Title: z.string().nullable().optional(),
  DocumentTypeArray: z.array(gwgDocumentTypeSchema),
  Completeness: gwgCompletenessSchema,
});

export type IGwGReminder = z.infer<typeof gwgReminderSchema>;
export type IGwGParticipant = z.infer<typeof gwgParticipantSchema>;
export type IGwGDocumentType = z.infer<typeof gwgDocumentTypeSchema>;
export type IGwGCompleteness = z.infer<typeof gwgCompletenessSchema>;
export type IGwGFolder = z.infer<typeof gwgFolderSchema>;

/**
 * Anforderung, wie sie zum Speichern zurückgeht. Für einen Ordner ist das Array
 * autoritativ: fehlende Zeilen werden im Backend gelöscht.
 */
export interface IGwGFolderRequirement {
  idGwGFolderRequirement: number;
  idLegalClientDocumentType: number;
  /** 0 = Standard des Ordners, sonst Override für diese juristische Person. */
  LegalClient_idLegalClient: number;
  isMandatory: boolean;
  ReminderArray: IGwGReminder[];
}

/** Im GwG-Ordner hochgeladene Version — landet beim jeweiligen Mandanten. */
export interface IGwGNewVersion {
  idLegalClient: number;
  idLegalClientDocumentType: number;
  FileArray: z.infer<typeof legalClientDocumentFileSchema>[];
  ValidUntil?: string | null;
  Note?: string | null;
}

/**
 * Änderungen an einem GwG-Ordner, die beim Vorgangs-Speichern mitgehen.
 *
 * Konfiguration ist autoritativ, Dokumente sind es bewusst **nicht**: der Ordner
 * sieht nur den GwG-Ausschnitt der Mandanten-Dokumente. Uploads sind daher
 * additiv, Löschungen explizit.
 */
export interface IGwGFolderSaveObject {
  idDocumentDirectory: number;
  RequirementArray: IGwGFolderRequirement[];
  NewVersionArray: IGwGNewVersion[];
  DeleteVersionIdArray: number[];
}

/** Nächste freie temporäre (negative) ID innerhalb einer Liste. */
export const getNextTempId = (idArray: number[]): number => {
  const minId = idArray.length > 0 ? Math.min(...idArray, 0) : 0;

  return minId >= 0 ? -1 : minId - 1;
};

/**
 * Anforderungen eines Ordners aus der berechneten Sicht zurückgewinnen —
 * Standardzeile je Dokumentart plus die Overrides einzelner Beteiligter.
 */
export const toRequirementArray = (
  folder: IGwGFolder
): IGwGFolderRequirement[] => {
  const requirementArray: IGwGFolderRequirement[] = [];

  folder.DocumentTypeArray.forEach(typeEntry => {
    requirementArray.push({
      idGwGFolderRequirement: typeEntry.idGwGFolderRequirement,
      idLegalClientDocumentType: typeEntry.idLegalClientDocumentType,
      LegalClient_idLegalClient: GWG_REQUIREMENT_FOLDER_DEFAULT,
      isMandatory: typeEntry.isMandatoryDefault,
      ReminderArray: typeEntry.ReminderArray,
    });

    typeEntry.ParticipantArray.filter(
      participant => participant.hasOverride
    ).forEach(participant => {
      requirementArray.push({
        idGwGFolderRequirement: participant.idGwGFolderRequirement,
        idLegalClientDocumentType: typeEntry.idLegalClientDocumentType,
        LegalClient_idLegalClient: participant.idLegalClient,
        isMandatory: participant.isMandatory,
        ReminderArray: participant.ReminderArray,
      });
    });
  });

  return requirementArray;
};

/** Datum aus einem ISO-Wert des Backends, ohne Zeitzonen-Umweg über `Date`. */
export const formatIsoDate = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }

  const [year, month, day] = value.slice(0, 10).split('-');

  return year === undefined || month === undefined || day === undefined
    ? ''
    : `${day}.${month}.${year}`;
};

/**
 * Erinnerung als Text, z. B. „7 Tage vor „Beurkundung" (17.09.2026)" oder
 * „am 15.09.2026".
 *
 * Bei Terminbezug wird das **gerechnete** Fälligkeitsdatum mit ausgegeben. Das
 * Backend liefert es als `DueDate` (Termin minus Vorlauf) und speichert es
 * bewusst nicht — verschiebt sich der Termin, wandert die Erinnerung mit. Ohne
 * die Ausgabe hier war genau das unsichtbar: die Anzeige blieb nach dem
 * Verschieben unverändert, obwohl im Hintergrund alles nachgezogen wurde
 * (QS #23, Punkt 6).
 *
 * Fehlt das Datum, hat der Termin keines oder wurde gelöscht — dann wird nicht
 * erinnert, und das muss man sehen.
 */
export const describeReminder = (reminder: IGwGReminder): string => {
  if (reminder.ReminderMode === GWG_REMINDER_MODE_MEETING) {
    const meetingName = reminder.LegalTransactionMeeting ?? 'Termin';
    const dueDate = formatIsoDate(reminder.DueDate);
    const suffix = dueDate === '' ? ' (Termin ohne Datum)' : ` (${dueDate})`;

    if (reminder.OffsetDays === 0) {
      return `am Tag des Termins „${meetingName}“${suffix}`;
    }

    return `${reminder.OffsetDays} Tage vor „${meetingName}“${suffix}`;
  }

  const remindAt = formatIsoDate(reminder.RemindAt);

  return remindAt === '' ? 'ohne Datum' : `am ${remindAt}`;
};
